import { useRef } from 'react';
import {
	Button as RACButton,
	Tooltip,
	TooltipTrigger,
} from 'react-aria-components';

import { cn } from '@/utils/cn';

import type { StrategyListItem } from '../../../../../bridge/types/strategy.ts';

import { DockSlot } from './DockSlot';
import { identityFor, type OrbIdentity } from './orbIdentity';
import { orbNow } from './orbRenderer';
import { DOCK_FRAME_TRANSITION_NAME } from './strategyDockAnimation';
import {
	dockIconStatus,
	strategyGroupOf,
	openFlags,
	requestsWaitingLabel,
	type StrategyGroupKey,
} from './strategyDockData';
import { StrategyOrb } from './StrategyOrb';

const GROUP_LABEL: Record<StrategyGroupKey, string> = {
	active: 'Active',
	paused: 'Paused',
	generating: 'Generating',
	new: 'New',
};

// The orb's spoken state, in the same four words the badge and the group headings use.
// A failed run is the one thing the lifecycle grouping cannot express, so it is called out.
function statusLabel(item: StrategyListItem, pendingProposals = 0): string {
	// The count rides in words, not in the dot. The dot is a wayfinding mark and cannot
	// carry a number, and this string reaches the tooltip and the accessible label together.
	const open = openFlags(item).length + pendingProposals;
	if (open > 0) return requestsWaitingLabel(open);
	if (item.status === 'failed') return 'Needs attention';
	return GROUP_LABEL[strategyGroupOf(item)];
}

// One dock entry = one strategy, drawn as a living orb wearing that strategy's own face and
// moving at the tempo of its status. Clicking opens the menu on this row.
function DockOrbButton({
	item,
	identity,
	entering,
	onOpen,
	pendingProposals = 0,
}: {
	item: StrategyListItem;
	identity: OrbIdentity;
	// This row just joined the dock: crossfade it up from the ghost palette (the slot coming
	// alive) and play a small settle. Ownership of "which rows are new" sits in StrategyDock,
	// which never remounts — deriving it from this component's own mount would replay the
	// entrance every time the menu opens.
	entering: boolean;
	onOpen: (id: string) => void;
	pendingProposals?: number;
}) {
	const status = dockIconStatus(item, pendingProposals);
	const label = item.wish?.trim() || 'Strategy';
	// When the orb was last pressed, on the shared animation clock. Held in a ref and read by the
	// canvas each frame: a press only needs to reach the renderer, and putting it in state would
	// re-render the row on every click for no visual reason.
	const pressedAt = useRef<number | null>(null);
	return (
		<TooltipTrigger delay={300}>
			<div className="relative">
				<RACButton
					data-orb-hover-owner
					data-testid={`strategy-dock-orb-${item.id}`}
					onPress={() => onOpen(item.id)}
					onPressStart={() => {
						pressedAt.current = orbNow();
					}}
					aria-label={`${label} — ${statusLabel(item, pendingProposals)}`}
					className={cn(
						`relative grid size-8 shrink-0 cursor-pointer place-items-center
						rounded-full transition-transform duration-150 outline-none
						hover:scale-110 active:scale-[0.94] motion-reduce:transition-none
						motion-reduce:hover:scale-100 motion-reduce:active:scale-100`,
						entering && 'animate-dock-orb-settle',
					)}
				>
					{({ isHovered }) => (
						<StrategyOrb
							identity={identity}
							status={status}
							fillIn={entering}
							interactive={{ hovered: isHovered, pressedAt: pressedAt.current }}
						/>
					)}
				</RACButton>
				{status.kind === 'attention' && (
					<span
						aria-hidden
						className="pointer-events-none absolute top-0 right-0 size-2.5
							rounded-full ring-2 ring-(--dock-fill)"
						style={{
							background: `radial-gradient(circle at 35% 30%, hsl(${identity.palette.h} ${identity.palette.s}% 72%), hsl(${identity.palette.h} ${identity.palette.s}% 45%) 55%, hsl(${identity.palette.h} ${identity.palette.s}% 28%))`,
						}}
					/>
				)}
			</div>
			<Tooltip
				placement="bottom"
				offset={6}
				className="z-50 max-w-xs rounded-md bg-gray-800 px-2.5 py-1.5 text-xs
					leading-snug text-white shadow-md"
			>
				{statusLabel(item, pendingProposals)}
			</Tooltip>
		</TooltipTrigger>
	);
}

// Wrapper that animates a dock orb in/out with the width-collapse idiom (grid 1fr<->0fr +
// negative-margin gap-cancel) so a strategy joining or leaving the dock slides.
//
// This animates WIDTH ONLY, and the orb inside owns its own settle. Running a scale/opacity
// pop while the clip is still opening crops the orb into a sliding sliver, and two rules
// fighting over `opacity` on nested nodes can strand an orb at opacity:0 in an occupied slot.
function CollapsibleOrb({
	visible,
	children,
}: {
	visible: boolean;
	children: React.ReactNode;
}) {
	return (
		<div
			className={cn(
				'grid overflow-hidden',
				`transition-[grid-template-columns,margin-right] duration-200 ease-out
				motion-reduce:transition-none`,
			)}
			style={{
				gridTemplateColumns: visible ? '1fr' : '0fr',
				marginRight: visible ? undefined : '-0.375rem',
			}}
			aria-hidden={!visible}
		>
			<div className={cn('min-w-0', !visible && 'pointer-events-none')}>
				{children}
			</div>
		</div>
	);
}

// The resting dock. Hangs FLUSH from the top edge of the chat header — its top sits at the
// header top, a soft gray fill — so it reads as chrome carved out of the header band rather
// than a card floating over it. The tab body is a flat rectangle; ogee "wing" Shoulders
// flush on each side flare it into the header with a smooth S-curve (see Shoulder), lifted
// from eragon.ai's nav notch. With nothing in the dock it collapses to a single ghost slot —
// an unfilled member of the collection, which starts creating. While the menu is open this bar is
// rendered ON TOP of the menu surface (they share the --dock-fill gray), so the dock and the menu
// read as one connected element.
// Shoulder = a flared "wing" beside the tab, lifted verbatim from eragon.ai's nav "Notch"
// (the exact reference). Each wing is an OGEE (S-curve), not a triangle or an arc: it is
// tangent-horizontal where it meets the flat header line at the top AND tangent-horizontal
// where it meets the tab's side at the bottom, with an inflection between. That double
// tangency is why it melds into both edges seamlessly instead of reading as a corner. The
// path below is eragon's own 48x42 LEFT wing (fill flush on the right edge, x=48, against
// the tab); the right wing is the same path mirrored via scaleX(-1). Fill tracks
// --dock-fill so it eases with the tab body on hover (the registered @property lets the
// color interpolate).
const NOTCH_VB_W = 48;
const NOTCH_VB_H = 42;
const NOTCH_LEFT_PATH =
	'M 48 42 C 43.184 42 38.962 39.385 34.997 35.436 C 31.032 31.487 27.339 26.221 23.575 20.944 C 19.815 15.67 15.984 10.387 11.752 6.423 C 8.194 3.091 4.342 0.683 0 0 L 48 0 Z';

// The tab's height in px, and the wing width that matches it. Keep the wing's native aspect
// ratio (48/42) at the tab's height or the ogee distorts and stops meeting the header line
// tangentially: SHOULDER_W = TAB_H * 48/42. TAB_H is py-1 (4+4) around a 32px orb button ⇒ 40,
// so 40 * 48/42 ≈ 46. Change the tab's vertical padding and this pair moves with it.
const TAB_H = 40;
const SHOULDER_W = Math.round((TAB_H * NOTCH_VB_W) / NOTCH_VB_H);

function Shoulder({ side }: { side: 'left' | 'right' }) {
	return (
		<svg
			aria-hidden
			viewBox={`0 0 ${NOTCH_VB_W} ${NOTCH_VB_H}`}
			preserveAspectRatio="none"
			className={cn(
				// Full tab height, top-aligned, hung just outside the tab on its side. A 1px
				// pull back into the tab guarantees the flush edge overlaps the tab fill (no
				// hairline seam).
				'pointer-events-none absolute inset-y-0 block h-full',
				side === 'left' ? 'right-full -mr-px' : 'left-full -ml-px',
			)}
			style={{
				width: SHOULDER_W,
				transform: side === 'right' ? 'scaleX(-1)' : undefined,
			}}
		>
			<path d={NOTCH_LEFT_PATH} style={{ fill: 'var(--dock-fill)' }} />
		</svg>
	);
}

export function DockBar({
	items,
	leavingItems = [],
	enteringIds,
	settled = true,
	onOpen,
	onCreate,
	isOpen,
	totalCount = 0,
	pendingProposalsByStrategyId = {},
}: {
	// The curated rows the menu also lists, so an orb can never point at a strategy the
	// list below it has dropped.
	items: StrategyListItem[];
	// Rows that just left the curated set, kept mounted for one collapse beat so a departure
	// is visible. Owned by StrategyDock (this component remounts when the menu opens).
	leavingItems?: StrategyListItem[];
	// Ids that just JOINED, which crossfade up from the ghost palette.
	enteringIds?: ReadonlySet<string>;
	// False until the strategy list is authoritative. Until then the bar shows no invite and
	// no entrance motion: the list starts empty, so an un-gated bar would advertise "no
	// strategies" on every page load and then play a fake arrival ceremony.
	settled?: boolean;
	// Opens the menu; an optional strategy id focuses that row's detail. Called with no id
	// from the manage affordance, which toggles the menu.
	onOpen: (id?: string) => void;
	// Starts the create-a-strategy flow (opens the menu on its create view).
	onCreate: () => void;
	// True while the menu is open. The bar is then rendered INSIDE the menu surface, so it must
	// neither set nor hover-shift --dock-fill: it INHERITS the menu's fill (the var is
	// `inherits: true`), which is what makes the dock and the menu read as one seamless surface.
	// Any local override here — even the hover shade — visibly splits the two.
	isOpen: boolean;
	// How many strategies the owner has AT ALL. `items` arrives already filtered to the running
	// ones, so the bar cannot tell "owns none" from "owns some, runs none" without this. Same
	// value and same name as DockMenu receives, so the two surfaces cannot disagree.
	totalCount?: number;
	// Pending-proposal count per strategy id. Read from the same state the strategy CARD uses,
	// so the collapsed orb and the open card agree about whether something is waiting.
	pendingProposalsByStrategyId?: Record<string, number>;
}) {
	// True when the bar has no orb to show, held back until the list is authoritative so a
	// loading dock never advertises anything.
	const barIsEmpty = settled && items.length === 0 && leavingItems.length === 0;
	// Where the empty slot goes, in three states. It IS the dock when the owner has NO strategies.
	// It TRAILS the filled orbs while the menu is open. It STANDS ALONE when the owner has
	// strategies and none of them is running — the state that used to say "Create Strategy" to an
	// owner holding 186 of them.
	const slotVariant: 'invite' | 'trailing' | null =
		barIsEmpty && totalCount === 0
			? 'invite'
			: settled && (barIsEmpty || isOpen)
				? 'trailing'
				: null;

	return (
		<div
			data-testid="strategy-dock-bar"
			style={{
				// Transition the registered --dock-fill color itself so the tab body AND its
				// ogee wings (which paint the same var) ease their shade together.
				transition: '--dock-fill 150ms ease',
				// The shared morph name: the bar carries it at REST, the menu surface carries it
				// while open, so the View Transition tweens one box growing out of / shrinking
				// back into the dock. Exactly ONE live node may carry a given name per captured
				// frame — hence the bar drops it while the menu holds it (the copy of this bar
				// rendered inside the menu is a plain, unnamed node).
				viewTransitionName: isOpen ? undefined : DOCK_FRAME_TRANSITION_NAME,
			}}
			className={cn(
				// The connected tab is a FLAT rectangle (no corner radius), exactly like
				// eragon's nav "Container": all the curvature comes from the ogee Shoulder
				// wings flush on each side, which leave the tab's bottom corners tangent-
				// horizontally so the flat bottom still reads as smoothly rounded. Rounding the
				// tab itself would pull its side in at the bottom and orphan the wing's edge
				// (the notch). Fill is the app-shell gray (#f8f8f8, RootLayout), exposed as
				// --dock-fill so the wings share it and its hover shade.
				`pointer-events-auto relative flex w-fit items-center gap-1.5
				rounded-none bg-(--dock-fill) px-3 py-1 whitespace-nowrap`,
				// Closed: the bar owns its fill (the app-shell gray) and brightens on hover.
				// Open: it sets NOTHING, inheriting the menu surface's fill so the dock and the
				// menu are exactly one color with no visible seam.
				!isOpen && '[--dock-fill:#f8f8f8] hover:[--dock-fill:#f0f0f0]',
			)}
		>
			<Shoulder side="left" />
			<Shoulder side="right" />
			{/* ONE structure for every state: [rows][slot?][divider][manage]. What the owner is
			    running leads; the route into the full list sits last. The manage affordance must
			    exist even with an empty dock — when it only rendered alongside rows, a user whose
			    strategies had all been curated out of the bar had no way to reach the list at all. */}
			{items.map((item) => (
				<CollapsibleOrb key={item.id} visible>
					<DockOrbButton
						item={item}
						identity={identityFor(item)}
						entering={Boolean(enteringIds?.has(item.id))}
						onOpen={(id) => onOpen(id)}
						pendingProposals={pendingProposalsByStrategyId[item.id] ?? 0}
					/>
				</CollapsibleOrb>
			))}
			{/* Rows that just left, collapsing out in place so the row closes up rather than
			    the orb vanishing between frames. */}
			{leavingItems.map((item) => (
				<CollapsibleOrb key={`leaving-${item.id}`} visible={false}>
					<DockOrbButton
						item={item}
						identity={identityFor(item)}
						entering={false}
						onOpen={() => {}}
					/>
				</CollapsibleOrb>
			))}
			{slotVariant && <DockSlot variant={slotVariant} onPress={onCreate} />}
			<span aria-hidden className="h-5 w-px bg-black/10" />
			<RACButton
				data-testid="strategy-dock-manage"
				onPress={() => onOpen()}
				aria-label="Manage strategies"
				className={cn(
					`grid size-7 shrink-0 cursor-pointer place-items-center rounded-full
					text-(--text-muted) transition-colors outline-none
					hover:bg-(--glass-bg-hover) hover:text-(--text-primary)`,
				)}
			>
				<GridGlyph />
			</RACButton>
		</div>
	);
}

// A compact 2x2 "manage" glyph — opens the takeover to see/manage all strategies.
function GridGlyph() {
	return (
		<svg viewBox="0 0 18 18" width={16} height={16} aria-hidden fill="none">
			<rect x="2.5" y="2.5" width="5" height="5" rx="1.5" fill="currentColor" />
			<rect
				x="10.5"
				y="2.5"
				width="5"
				height="5"
				rx="1.5"
				fill="currentColor"
				opacity="0.55"
			/>
			<rect
				x="2.5"
				y="10.5"
				width="5"
				height="5"
				rx="1.5"
				fill="currentColor"
				opacity="0.55"
			/>
			<rect
				x="10.5"
				y="10.5"
				width="5"
				height="5"
				rx="1.5"
				fill="currentColor"
			/>
		</svg>
	);
}
